import React, { useCallback } from "react";
import PricingTier from "./PricingTier";
import "./styles/Pricing.css";
import { NavSections } from "../common/nav-sections";
import { useOnBuyClick, useOnGetBillingPortal } from "../common/paymentUtils";
import { Plan } from "../services/UserService";
import { useAuth } from "../providers/AuthContext";

interface IPricingProps {
  plan: Plan
}

const Pricing: React.FC<IPricingProps> = ({ plan }) => {
  const auth = useAuth();
  const onBuyClick = useOnBuyClick();
  const onGetBillingUrlClick = useOnGetBillingPortal();

  const priceButtonText = useCallback((val: string, p: Plan) => {
    if (plan === p && auth.user) {
      return 'Your plan'
    } else {
      return val;
    }
  }, [plan, auth.user]);

  const onClick = useCallback(async (priceId: string) => {
    if (plan === Plan.Free) {
      await onBuyClick(priceId);
    } else {
      await onGetBillingUrlClick();
    }
  }, [plan, onBuyClick, onGetBillingUrlClick]);

  return (
    <>
      <section className="pricing-section" id={NavSections.PRICING}>
        <h2 className="pricing-heading">Pricing</h2>
        <p className="pricing-subtitle">
          Cut, convert and automate your video workflow. <br />
          Pay only for the minutes you actually process.
        </p>

        <div className="pricing-grid">
          {auth.user && plan !== Plan.Free
            ? <></> :
            <PricingTier
              title="Free"
              price="0"
              features={[
                { label: "3 processing minutes / month", included: true },
                { label: "1 concurrent job", included: true },
                { label: "up to 720p support", included: true },
                { label: '1 hour job retention', included: true },
                { label: '500MB max file size', included: true},
                { label: "No API access", included: false },
                { label: "4k support", included: false },
              ]}
              buttonText={priceButtonText("Get Started For Free", Plan.Free)}
              // TODO: replace with real signup navigation if needed
              onClick={() => {
                // e.g. navigate("/signup");
              }}
            />}
          {/* BASIC TIER */}
          <PricingTier
            title="Basic"
            price="10"
            features={[
              { label: "120 processing minutes / month", included: true },
              { label: "2 concurrent jobs", included: true },
              { label: "up to 1080p support", included: true },
              { label: '1 day job retention', included: true },
              { label: '2GB max file size', included: true},
              { label: "No API access", included: false },
              { label: "4k support", included: false },

            ]}
            buttonText={priceButtonText("Switch to Basic", Plan.Basic)}
            onClick={() =>
              onClick(process.env.REACT_APP_BASIC_TIER_ID ?? "")
            }
          />

          {/* PRO TIER (HIGHLIGHTED) */}
          <PricingTier
            title="Pro"
            oldPrice="35"
            price="30"
            tag="Most Popular"
            highlight
            features={[
              { label: "400 processing minutes / month", included: true },
              { label: "3 concurrent jobs", included: true },
              { label: "up to 4k support", included: true },
              { label: '10 days job retention', included: true },
              { label: '4GB max file size', included: true},
              { label: "1000 api requests / month", included: true },
              { label: "Webhook callbacks & integrations", included: true },
            ]}
            buttonText={priceButtonText("Switch to Pro", Plan.Pro)}
            onClick={() =>
              onClick(process.env.REACT_APP_PRO_PLAN_PRICE_ID ?? "")
            }
          />

          {/* DEVELOPER / API TIER */}
          <PricingTier
            title="Developer"
            price="200"
            tag="For Teams & APIs"
            features={[
              { label: "1500 processing minutes / month", included: true },
              { label: "5 concurrent jobs", included: true },
              { label: "up to 4k support", included: true },
              { label: '30 days job retention', included: true },
              { label: '8GB max file size', included: true},
              { label: "10000 api requests / month", included: true },
              { label: "Webhook callbacks & integrations", included: true },
            ]}
            buttonText={priceButtonText("Switch to Dev", Plan.Developer)}
            onClick={() =>
              onClick(process.env.REACT_APP_DEVELOPER_PLAN_PRICE_ID ?? "")
            }
          />
        </div>
      </section>
    </>
  );
};

export default Pricing;